import type { Capabilities, MeResponse, Membership, WorkspaceRef } from "@ondo/shared";

import { About } from "../about/About.tsx";
import { Activity } from "../activity/Activity.tsx";
import { Profile } from "../profile/Profile.tsx";
import { RequestRepresentation } from "../representations/RequestRepresentation.tsx";
import { ShareCodes } from "../shareCodes/ShareCodes.tsx";
import { readAgentIssuableWorks } from "../shareCodes/useShareCodesController.ts";
import { NotBuiltYet } from "../shell/NotBuiltYet.tsx";
import { PortalShell } from "../shell/PortalShell.tsx";
import { Notifications } from "../notifications/Notifications.tsx";
import { NotificationPreferences } from "../notificationPreferences/NotificationPreferences.tsx";
import { atLeast } from "@ondo/shared";
import { inboxPrincipalFor } from "../shell/shell.model.ts";
import { Verifications } from "../verifications/Verifications.tsx";
import { WorkRecord } from "../work/WorkRecord.tsx";
import { Representations } from "./Representations.tsx";
import { AGENT_COPY, AGENT_PORTAL } from "./agent.constants.ts";
import type { AgentDestination } from "./agent.constants.ts";
import type { PortalView } from "../shell/shell.model.ts";

/**
 * The agent's workspace.
 *
 * The frame is `PortalShell`, shared with the collector; what is here is which
 * component each destination renders — the one thing that genuinely differs
 * between two portals.
 *
 * Four of its screens are the collector's, unchanged and unwrapped. That is the
 * point rather than a shortcut: what an agent may do to a work is decided in
 * SQL, so a second, agent-shaped copy of the work record or the verification
 * inbox would be a second opinion about that. The same components render,
 * narrowed by the same policies.
 */
export function AgentWorkspace({
  destination,
  record,
  workspace,
  principal,
  capabilities,
  membership,
  canSwitch,
  onSignOut,
}: {
  destination: AgentDestination;
  /** Which work a `work` destination is about. Null for every other one. */
  record: string | null;
  workspace: WorkspaceRef;
  principal: MeResponse["principal"];
  capabilities: Capabilities;
  /** The caller's membership of this workspace's organisation, or null. */
  membership: Membership | null;
  canSwitch: boolean;
  onSignOut: () => void;
}) {
  return (
    <PortalShell
      spec={AGENT_PORTAL}
      destination={destination}
      workspace={workspace}
      principal={principal}
      capabilities={capabilities}
      membership={membership}
      canSwitch={canSwitch}
      onSignOut={onSignOut}
    >
      {(view) => (
        <Destination
          destination={destination}
          record={record}
          workspace={workspace}
          capabilities={capabilities}
          membership={membership}
          principal={principal}
          view={view}
          onSignOut={onSignOut}
        />
      )}
    </PortalShell>
  );
}

/** The one place a switch over the agent's destinations lives. */
function Destination({
  destination,
  record,
  workspace,
  capabilities,
  membership,
  principal,
  view,
  onSignOut,
}: {
  destination: AgentDestination;
  record: string | null;
  workspace: WorkspaceRef;
  capabilities: Capabilities;
  /**
   * The caller's rung on this firm, or null on a personal workspace.
   *
   * Read ONLY through `atLeast`, and only to decline offering a control the
   * database will refuse. A capability answers "may this account do this at
   * all"; a rung answers "may this SEAT do it here".
   */
  membership: Membership | null;
  principal: MeResponse["principal"];
  view: PortalView;
  onSignOut: () => void;
}) {
  switch (destination) {
    // `orgOndoId` is the FIRM or null, not `inboxPrincipalFor`'s fallback to the
    // reader: it decides which membership the "Logged in as" card names, and on
    // a personal workspace there is no seat to name.
    case "representations":
      return (
        <Representations
          workBase={view.hrefs.workBase}
          orgOndoId={workspace.organisation?.ondoId ?? null}
        />
      );

    // Scoped to the FIRM on an organisation's workspace and to the person on a
    // personal one, the same resolution the inbox below uses — and here it
    // decides who would HOLD the mandate rather than merely which inbox is
    // shown. SQL checks the caller is an `admin` of whatever this resolves to,
    // so a junior member of an advisory cannot commit the firm — and `canAsk`
    // now asks the same question before the form is drawn, so they meet the
    // sentence instead of the refusal. The SQL check is still the one that
    // decides; this only stops the screen offering a press it cannot honour.
    // The grant shape the rail waited on — see 20260910123000. Issuing is the
    // editor rung, because a code widens a work's audience; the ledger is
    // every member's to read. `asOndoId` is the firm on a firm's workspace,
    // resolved as Request Representation resolves its own.
    case "share-codes":
      return (
        <ShareCodes
          workBase={view.hrefs.workBase}
          asOndoId={workspace.organisation?.ondoId ?? null}
          canIssue={atLeast(membership?.level, "editor")}
          cannotIssue={AGENT_COPY.shareCodesNeedEditor}
          // The mandates, which is what an agent may issue for. The collector's
          // rail mounts the same screen over the works they OWN — see
          // `readOwnedIssuableWorks`, and why the source is a parameter at all.
          readIssuableWorks={readAgentIssuableWorks}
        />
      );

    case "request-representation":
      return (
        <RequestRepresentation
          principalOndoId={inboxPrincipalFor(principal, workspace)}
          canAsk={atLeast(membership?.level, "admin")}
        />
      );

    // The FIRM's inbox on an organisation workspace, the reader's own on a
    // personal one — `inboxPrincipalFor`, exactly as Verifications above.
    //
    // `workspace` AND an id, which look redundant and are not: the workspace
    // decides where each row's action LINKS to, and the id decides whose feed
    // is read. On a firm workspace those are two different principals, and
    // conflating them is what an inbox showing a member's own empty feed under
    // the firm's heading would look like.
    case "notifications":
      return (
        <Notifications
          workspace={workspace}
          inboxOndoId={inboxPrincipalFor(principal, workspace)}
        />
      );

    // The FIRM's preferences on a firm workspace, exactly as the inbox above is
    // the firm's inbox — and writable only by somebody who administers it,
    // because choosing how an organisation is told things is an act of the
    // organisation. `ondo.set_notification_preference()` refuses it either way;
    // this is what stops the screen offering a control that would be refused.
    case "notification-preferences":
      return (
        <NotificationPreferences
          principalOndoId={inboxPrincipalFor(principal, workspace)}
          canWrite={atLeast(membership?.level, "admin")}
          isFirm={workspace.organisation !== null}
        />
      );

    case "about":
      return <About />;

    // The PERSON's own ONDO id, even in a firm's workspace, so their acts read
    // "You" rather than their name. That is the opposite of the inbox below it
    // and deliberately so: an inbox is addressed to a principal, while a trail
    // is read by a reader, and the reader is always the person signing in.
    case "activity":
      // `administers` CHANGES NO ROWS. The trail's contents are decided by
      // `access_events_read` — a viewer's own reads, an administrator's whole
      // firm — and this only picks which sentence an EMPTY trail gets, so an
      // ordinary seat is never told the firm has opened nothing when the truth
      // is that they have. `atLeast` is the same derivation the rest of this
      // file uses for a rung; no new capability was invented for it.
      return (
        <Activity
          workBase={view.hrefs.workBase}
          viewerOndoId={principal.ondoId}
          administers={atLeast(membership?.level, "admin")}
        />
      );

    // Scoped to the FIRM on an organisation's workspace and to the person on a
    // personal one — see `inboxPrincipalFor`, which is where the argument is.
    case "verifications":
      return <Verifications principalOndoId={inboxPrincipalFor(principal, workspace)} />;

    // Takes the workspace rather than the principal, because WHOSE profile this
    // is follows from whether the workspace has an organisation — a personal one
    // opens onto the reader, the firm's onto the firm and its members.
    case "profile":
      return <Profile workspace={workspace} onSignOut={onSignOut} />;

    // `record` is non-null here by construction: resolveWorkspaceRoute sends a
    // work destination without one back to the representations list.
    case "work":
      return record ? (
        <WorkRecord
          workId={record}
          // An agent holds no collection, so "back to the collection" is not a
          // place they have. The list of what they represent is.
          collectionHref={view.hrefs.recordFallback}
          // An agent owns no work, so the server says false and the panel hides
          // itself. Passed rather than hard-coded, because the answer is the
          // server's: an account can hold a mandate on one work and own another.
          canAssignAgent={capabilities.assignAgent}
          canConsign={capabilities.consignWork}
          canWriteHistory={atLeast(membership?.level, "editor")}
          // OND-68. An agent may hold a seat at a firm or act alone, so this is
          // null as often as not, and that is the honest answer rather than a
          // missing one.
          actingAs={workspace.organisation?.ondoId ?? null}
        />
      ) : null;

    default:
      return <NotBuiltYet copy={view.notYet} />;
  }
}
